import { cn } from "@/lib/utils";
import {
	Card,
	CardContent,
	CardFooter,
	CardHeader,
	Typography,
} from "@st-marketplace/ui";
import { type ReactNode, createContext } from "react";

/**
 * "rows": label beside control. "columns": the same, but two settings per row on xl+.
 * "compact" (side column): label above control, smaller text.
 */
export type SettingsCardLayout = "rows" | "columns" | "compact";
export const SettingsLayoutContext = createContext<SettingsCardLayout>("rows");

type SettingsCardProps = {
	title: ReactNode;
	description?: ReactNode;
	children: ReactNode;
	footer?: ReactNode;
	layout?: SettingsCardLayout;
};

export const SettingsCard: React.FC<SettingsCardProps> = ({
	title,
	description,
	children,
	footer,
	layout = "rows",
}) => {
	const compact = layout === "compact";
	return (
		<SettingsLayoutContext.Provider value={layout}>
			<Card>
				<CardHeader className="border-b border-secondary-border">
					<Typography variant={compact ? "title-sm" : "title-md"} as="h2">
						{title}
					</Typography>
					{description ? (
						<Typography
							variant={compact ? "caption" : "body-sm"}
							className="text-secondary-text-100"
						>
							{description}
						</Typography>
					) : null}
				</CardHeader>
				<CardContent
					className={
						layout === "columns"
							? "divide-y divide-secondary-border xl:grid xl:grid-cols-2 xl:gap-x-10 xl:gap-y-6 xl:divide-y-0"
							: "divide-y divide-secondary-border"
					}
				>
					{children}
				</CardContent>
				{footer ? (
					<CardFooter className="mt-0 justify-end border-t border-secondary-border pt-5">
						{footer}
					</CardFooter>
				) : null}
			</Card>
		</SettingsLayoutContext.Provider>
	);
};

type SettingsSubheadingProps = {
	className?: string;
	title: ReactNode;
	description?: ReactNode;
};

/** Groups a set of rows inside a card. */
export const SettingsSubheading: React.FC<SettingsSubheadingProps> = ({
	className,
	title,
	description,
}) => (
	<div className={cn("col-span-full pt-8 pb-5 xl:pt-4 xl:pb-0", className)}>
		<Typography variant="title-sm">{title}</Typography>
		{description ? (
			<Typography variant="body-sm" className="mt-1 text-secondary-text-100">
				{description}
			</Typography>
		) : null}
	</div>
);
